import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const cards = [
  {
    title: 'Leaders in small and mid-sized companies',
    pain: 'Good people leave before you see it coming',
    payoff: 'You get an early read on where commitment is slipping. Time to act while it is still a conversation, not a resignation.',
  },
  {
    title: 'PE-backed companies and portfolios',
    pain: 'People risk surfaces late in the numbers',
    payoff: 'The same read across every company in the portfolio. Leadership and retention risk becomes visible early enough to price and manage.',
  },
  {
    title: 'HR and CHRO leaders',
    pain: 'You are asked for evidence, not anecdotes',
    payoff: 'Confidential answers turned into clear evidence. You walk into the executive table with something that holds up.',
  },
  {
    title: 'Post-merger integration',
    pain: 'Two ways of working quietly pulling apart',
    payoff: 'See where legacy teams are drifting from each other. Fix the friction while integration is still in motion.',
  },
  {
    title: 'Scale-ups past 100 people',
    pain: 'Structure strains faster than it shows',
    payoff: 'Know which teams are carrying too much and where clarity has been lost. Adjust before growth starts to cost you.',
  },
  {
    title: 'Frontline and shift teams',
    pain: 'The floor is rarely heard in time',
    payoff: 'Give shift and frontline teams a confidential voice. You hear what is affecting turnover before it becomes a pattern.',
  },
  {
    title: 'New leaders in seat',
    pain: 'Ninety days and no real picture',
    payoff: 'An honest view of the team from week one. You lead on evidence instead of first impressions.',
  },
  {
    title: 'Change and transformation',
    pain: 'Disruption can cost you momentum',
    payoff: 'Track how people are absorbing the change as it happens. Adjust the pace and the message before delivery suffers.',
  },
];

export function AudienceCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const child = el.firstElementChild as HTMLElement | null;
    if (!child) return;
    const step = child.offsetWidth + 16;
    setActive(Math.min(cards.length - 1, Math.round(el.scrollLeft / step)));
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const scrollTo = (index: number) => {
    const el = trackRef.current;
    const child = el?.children[index] as HTMLElement | undefined;
    if (!el || !child) return;
    el.scrollTo({ left: child.offsetLeft - el.offsetLeft, behavior: 'smooth' });
  };

  const nudge = (dir: -1 | 1) => scrollTo(Math.max(0, Math.min(cards.length - 1, active + dir)));

  return (
    <div>
      <div
        ref={trackRef}
        className="flex items-stretch gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-6 px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {cards.map((card) => (
          <article
            key={card.title}
            className="relative snap-start shrink-0 w-[300px] min-h-[280px] overflow-hidden rounded-[6px] border border-warm-neutral/60 bg-card p-8 pt-9 flex flex-col shadow-[0_1px_2px_hsl(20_14%_6%/0.04),0_8px_24px_-16px_hsl(20_14%_6%/0.18)]"
          >
            <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-brand" />
            <h3 className="font-display text-xl font-bold text-ink tracking-[-0.015em] leading-snug text-balance min-h-[56px]">{card.title}</h3>
            <p className="mt-3 text-sm font-semibold text-brand font-grotesk leading-relaxed min-h-[46px]">{card.pain}</p>
            <span aria-hidden className="mt-2 block h-px w-8 bg-warm-neutral shrink-0" />
            <p className="mt-4 text-sm text-charcoal/75 font-grotesk leading-relaxed text-pretty">{card.payoff}</p>
          </article>
        ))}
      </div>


      <div className="mt-4 flex items-center gap-4">
        <div className="flex gap-2">
          <button
            type="button"
            aria-label="Previous"
            onClick={() => nudge(-1)}
            disabled={active === 0}
            className="h-9 w-9 rounded-full border border-hairline text-ink flex items-center justify-center transition-opacity disabled:opacity-30 hover:bg-ink/5"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={() => nudge(1)}
            disabled={active === cards.length - 1}
            className="h-9 w-9 rounded-full border border-hairline text-ink flex items-center justify-center transition-opacity disabled:opacity-30 hover:bg-ink/5"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="flex gap-1.5">
          {cards.map((card, i) => (
            <button
              key={card.title}
              type="button"
              aria-label={`Go to ${card.title}`}
              onClick={() => scrollTo(i)}
              className={`h-1.5 rounded-full transition-all ${i === active ? 'w-6 bg-brand' : 'w-1.5 bg-ink/20'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
